import { Injectable, effect, inject, untracked } from '@angular/core';
import { Store } from '@ngrx/store';
import mapboxgl from 'mapbox-gl';

import { FilterMethod } from '@core/models/data-visualization/data-visualization.state';
import { ResolvedFocus } from '@core/services/visual-focus/visual-focus.model';
import { VisualFocusService } from '@core/services/visual-focus/visual-focus.service';
import { selectAggregatorFilter } from '@core/store/data-visualization/data-visualization.selectors';
import { isNil } from '@core/utils/shared-utils';

import { MapRegistry } from '@features/components/visualization/mapbox/services/registry.service';

/**
 * The look of a stop that is not the one you are reading — pushed away by a focus, or left out
 * by a hard filter. One spec, exported because two layers wear it: the focus-driven `dim` layer
 * and the filter-driven `unselected` one.
 *
 * **A dot, not a pin.** A pin says "go here", and a depot carrying eighty of them says it eighty
 * times over the single route the focus exists to surface. A 3px dot at 45% says only "a place
 * exists here", which is the whole content of "unlit". Both layers are therefore `circle` layers:
 * circles take no part in symbol collision, so an unlit stop can neither be culled nor cull the
 * lit pin standing on it, and they need none of the `readsState` care a symbol layer does.
 */
export const UNLIT_MARKER_COLOR = '#9aa3af';
export const UNLIT_DOT_OPACITY = 0.45;
/** Small enough to read as a place rather than a destination, at every zoom worth reading. */
export const UNLIT_DOT_RADIUS: mapboxgl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  9,
  2.5,
  14,
  3.5
];
/**
 * What a cluster icon comes to under any focus: gone.
 *
 * A cluster stands for stops of many routes at once, so it can never be lit — a focus has
 * nothing to say about it except that it is in the way. Faded rather than dropped it still
 * blankets the map: a few dozen bubbles at even a token opacity read as a field of circles
 * over the one route the focus exists to surface, and the count labels stay legible enough to
 * scan. What the focus leaves on the map is the route it lights and the stops belonging to it.
 */
const CLUSTER_FOCUS_OPACITY = 0;
/** What the icons an open spider is drawn over recede to while it is open. */
const SPIDERFY_BACKDROP_OPACITY = 0.6;

/** The feature-state key every focus expression reads. */
const STATE_KEY = 'focus';
/**
 * The key that decides whether a stop draws its number. Its own key rather than a tier, because
 * the two answer different questions: a stop of the displayed route is numbered whatever the
 * focus has done to *it* — an action focus dims every stop but the one it names, and their
 * numbers stay, which is what keeps the route readable as a sequence while a stop is picked out.
 */
const NUMBERED_KEY = 'numbered';
/** The state of a feature no focus has named. `match` falls through to the layer's own value. */
const REST = 'rest';

type FocusTier = 'lit' | 'soft' | 'dim';
type PaintProperty =
  | 'line-opacity'
  | 'icon-opacity'
  | 'text-opacity'
  | 'circle-opacity'
  | 'icon-halo-width'
  | 'text-halo-width';
type StyleValue = mapboxgl.PaintSpecification[PaintProperty];

/**
 * A layer whose paint the focus drives, and the expression it holds while attached.
 *
 * The expression is *static*: it reads each feature's tier from feature state, so a focus
 * change writes state and never touches the style. That is the whole point — see the class note.
 */
interface FocusTarget {
  layerId: string;
  property: PaintProperty;
  /**
   * Mapbox's own default for this property, used as the baseline when the layer declares
   * nothing. It cannot be left `undefined`: the baseline goes on to sit in an expression branch,
   * and a branch holding `undefined` fails validation, which makes mapbox drop the write without
   * an error.
   */
  unset: StyleValue;
  /** The expression to install, given what the layer was declared with. */
  expression(baseline: StyleValue): StyleValue;
}

/**
 * Paints the linked focus onto the map.
 *
 * **Every focus change is a feature-state write and nothing else.** This is not a stylistic
 * choice: mapbox's `Style.setLayoutProperty` always calls `_updateLayer`, and `setPaintProperty`
 * does whenever the value crosses between a constant and an expression — which painting a focus
 * onto a declared value always does. `_updateLayer` marks the layer's source cache `reload` and
 * pauses it, so one repainted focus re-parses and re-lays out every loaded tile of the route and
 * marker sources. Measured on a plan of 100 routes and 463 stops that is ~320ms of map churn per
 * hover, which is why the focus used to be debounced before it was allowed to reach the map at
 * all. The same focus expressed as feature state costs ~1ms and forces no reload, so the hover
 * lands on the next frame and needs no settle delay.
 *
 * What follows from that: the expressions here are installed once at attach and read
 * `['feature-state', 'focus']`. Nothing outside `installExpressions` may write these properties,
 * with one exception — `MapFilterService` owns `markers.selected`'s `icon-opacity` while
 * `BY_HIGHLIGHT_MARKER` is active, so that layer is left alone until the filter lets go of it.
 *
 * Two things a focus cannot say through feature state, and how they are handled instead:
 * - **Draw order.** `line-sort-key` and `symbol-sort-key` are layout properties, and mapbox
 *   rejects feature-state expressions there outright. Order is settled structurally instead: an
 *   unlit route is not drawn at all, and an unlit stop is drawn as a dot from the `dim` layer,
 *   which sits *under* every route layer.
 * - **Which icon a stop uses.** Also layout, which is why the unlit look is a separate layer
 *   rather than an image swap on the layer already drawing the stop.
 */
@Injectable({ providedIn: 'root' })
export class MapFocusService {
  private readonly visualFocus = inject(VisualFocusService);
  private readonly store = inject(Store);
  private readonly aggregatorFilter = this.store.selectSignal(selectAggregatorFilter);

  private map?: mapboxgl.Map;
  private registry?: MapRegistry;
  private targets: FocusTarget[] = [];
  private readonly baseline = new Map<string, StyleValue>();
  /** The sources a route uid addresses, and the ones an action uid does. */
  private routeSources: string[] = [];
  private markerSources: string[] = [];
  /**
   * The one source whose stops can carry a number: the unclustered one the label layer draws.
   * The tiers below go to every marker source, because every one of them has a layer painting
   * them; `NUMBERED_KEY` written to the other two would be a state nothing reads.
   */
  private numberedSource: string | undefined;
  /** The tier each feature was last given, so a focus change writes only what moved. */
  private readonly paintedRouteTiers = new Map<string, FocusTier>();
  private readonly paintedActionTiers = new Map<string, FocusTier>();
  /** The stops currently carrying a number, for the same reason. */
  private numbered: ReadonlySet<string> = new Set();
  private spiderfyOpen = false;
  /** Whether the stops are currently drawn as clusters. `MapMarkers` owns the answer. */
  private grouped = true;
  /**
   * Identity, not equality: `focus` is a computed, so an unchanged focus is the same object.
   * Starts at `null` so the first resolution of "no focus" writes nothing.
   */
  private painted: ResolvedFocus | null = null;
  /** Whether `MapFilterService` is currently holding `markers.selected`'s opacity. */
  private filterOwnsSelected = false;

  constructor() {
    effect(() => {
      const focus = this.visualFocus.focus();
      const standDown =
        this.aggregatorFilter().filterMethod === FilterMethod.BY_HIGHLIGHT_MARKER;
      untracked(() => this.paint(standDown ? null : focus));
    });

    // Which stops are numbered follows from the *selection* as much as from the focus — one
    // route selected in the table is as much a single route on display as one hovered — so it
    // is watched on its own rather than folded into the paint above.
    effect(() => {
      const numbered = this.visualFocus.numberedActionUids();
      untracked(() => this.writeNumbered(numbered));
    });

    // `MapFilterService` writes its own `icon-opacity` onto `markers.selected` under
    // `BY_HIGHLIGHT_MARKER`, overwriting the focus expression. Hand the layer its expression
    // back the moment that filter lets go, or it keeps the filter's value for good.
    effect(() => {
      const owned =
        this.aggregatorFilter().filterMethod === FilterMethod.BY_HIGHLIGHT_MARKER;
      untracked(() => {
        const released = this.filterOwnsSelected && !owned;
        this.filterOwnsSelected = owned;
        if (released) this.reinstallSelected();
      });
    });
  }

  attach(map: mapboxgl.Map, registry: MapRegistry): void {
    this.map = map;
    this.registry = registry;
    // `attach()` runs on style load, before any cluster can be expanded, so the expressions
    // installed below are always the spider-closed ones.
    this.spiderfyOpen = false;
    this.paintedRouteTiers.clear();
    this.paintedActionTiers.clear();
    this.baseline.clear();
    this.painted = null;

    this.routeSources = [registry.routes.source.default];
    this.markerSources = [
      registry.markers.source.default,
      registry.markers.source.clustered,
      registry.markers.source.spiderfy
    ];
    this.numberedSource = registry.markers.source.default;
    this.targets = this.buildTargets(registry);

    this.numbered = new Set();
    this.captureBaselines();
    this.installExpressions();
    untracked(() => this.writeNumbered(this.visualFocus.numberedActionUids()));

    // Imperatively rather than through the effect: the effect writes only on a *change* of
    // focus, and any run of it that landed before the style loaded found no map and was dropped.
    // A focus that predates the map is the normal case for a detached popup, whose replicated
    // pin arrives on the register handshake while its style is still being fetched.
    untracked(() => {
      const standDown =
        this.aggregatorFilter().filterMethod === FilterMethod.BY_HIGHLIGHT_MARKER;
      this.paint(standDown ? null : this.visualFocus.focus());
    });
  }

  /**
   * Must run before `map.remove()`: restoring the layers reads them back off the map. Safe to
   * call with nothing attached, so the map component can call it unconditionally rather than
   * behind a test that would let a live attachment survive its map.
   */
  detach(): void {
    this.writeNumbered(new Set());
    this.clearTiers();
    this.restoreBaselines();
    this.map = undefined;
    this.registry = undefined;
    this.targets = [];
    this.routeSources = [];
    this.markerSources = [];
    this.numberedSource = undefined;
    this.baseline.clear();
    this.painted = null;
  }

  /**
   * Tells the painter whether an expanded cluster is open. `MapMarkers` owns that state and the
   * layers' visibility; their `icon-opacity` is this service's to write, so the spider's own
   * dimming arrives here as one more input to the expressions.
   *
   * This one *does* rewrite the style, because what a spider changes is the value a lit stop
   * takes, which is not a per-feature fact. It is a click, and rare, so the reload it costs is
   * affordable — unlike one per pointer move.
   */
  /**
   * Tells the painter whether the stops are grouped into clusters.
   *
   * Grouping decides what an *unfocused* stop is worth on the unclustered `default` layer. That
   * layer holds every stop the map has, and in grouped mode the clusters are what stands for
   * them, so at rest it draws nothing. Under a focus the clusters come off the map — a bubble
   * spans routes and can never be lit — and this layer is what puts the focused route's own
   * stops back, since a stop swallowed by a cluster is drawn by no other layer.
   *
   * Like `setSpiderfyOpen`, this rewrites the style rather than writing state: what it changes
   * is a value, not a fact about one feature. It is a toggle in the map controls, so the reload
   * it costs is one a click can afford.
   */
  setGrouping(grouped: boolean): void {
    if (this.grouped === grouped) return;
    this.grouped = grouped;
    this.installExpressions();
  }

  setSpiderfyOpen(open: boolean): void {
    if (this.spiderfyOpen === open) return;
    this.spiderfyOpen = open;
    this.installExpressions();
    this.writeClusterOpacity(this.painted);
  }

  /**
   * Turns the stop numbers on and off, one feature at a time. Through state rather than the
   * layer's `visibility`, for two reasons: the label layer holds every stop on the map, so
   * visibility alone would number the whole plan rather than the one route on display; and
   * `setLayoutProperty` is exactly the write this service exists to avoid.
   */
  private writeNumbered(numbered: ReadonlySet<string>): void {
    const source = this.numberedSource;
    if (isNil(this.map) || this.map._removed || isNil(source)) {
      this.numbered = numbered;
      return;
    }

    for (const uid of numbered) {
      if (this.numbered.has(uid)) continue;
      this.map.setFeatureState({ source, id: uid }, { [NUMBERED_KEY]: true });
    }

    for (const uid of this.numbered) {
      if (numbered.has(uid)) continue;
      this.map.removeFeatureState({ source, id: uid }, NUMBERED_KEY);
    }

    this.numbered = new Set(numbered);
  }

  private paint(focus: ResolvedFocus | null): void {
    if (isNil(this.map) || this.map._removed || focus === this.painted) return;
    this.painted = focus;
    this.writeTiers(focus);
    this.writeClusterOpacity(focus);
  }

  /**
   * The tier every known feature takes under this focus, written to feature state.
   *
   * Only what moved: re-tiering the two routes a pointer crossed is a couple of writes, where
   * the whole plan would be a few hundred. Both are cheap; this keeps the common case free.
   */
  private writeTiers(focus: ResolvedFocus | null): void {
    if (focus === null) {
      this.clearTiers();
      return;
    }

    // Only when the focus lights a line the map can actually draw. `dim` means "not drawn", so
    // dimming against a lit set that is filtered away — or that names no route on this map at
    // all — would take every line off the screen and leave nothing lit in their place. An
    // empty map here restores the routes to rest, which is the right answer for a focus that
    // has nothing to say about them.
    const litRoutes = this.visualFocus.litDrawableRouteUids();
    const routeTiers = new Map<string, FocusTier>();
    if (litRoutes.size > 0) {
      for (const uid of this.visualFocus.knownRouteUids()) {
        // Soft and dim are the same thing for a line: a route the focus does not name is not
        // drawn at all, which is what keeps the lit one readable in a corridor of a hundred.
        routeTiers.set(uid, litRoutes.has(uid) ? 'lit' : 'dim');
      }
    }

    const actionTiers = new Map<string, FocusTier>();
    for (const uid of this.visualFocus.knownActionUids()) {
      actionTiers.set(
        uid,
        focus.litActionUids.has(uid)
          ? 'lit'
          : focus.softActionUids.has(uid)
            ? 'soft'
            : 'dim'
      );
    }

    this.applyTiers(this.routeSources, routeTiers, this.paintedRouteTiers);
    this.applyTiers(this.markerSources, actionTiers, this.paintedActionTiers);
  }

  private applyTiers(
    sources: readonly string[],
    next: ReadonlyMap<string, FocusTier>,
    painted: Map<string, FocusTier>
  ): void {
    if (isNil(this.map)) return;

    for (const [uid, tier] of next) {
      if (painted.get(uid) === tier) continue;
      for (const source of sources) {
        this.map.setFeatureState({ source, id: uid }, { [STATE_KEY]: tier });
      }
    }

    for (const uid of painted.keys()) {
      if (next.has(uid)) continue;
      for (const source of sources) {
        this.map.removeFeatureState({ source, id: uid }, STATE_KEY);
      }
    }

    painted.clear();
    for (const [uid, tier] of next) painted.set(uid, tier);
  }

  private clearTiers(): void {
    if (isNil(this.map) || this.map._removed) {
      this.paintedRouteTiers.clear();
      this.paintedActionTiers.clear();
      return;
    }

    this.applyTiers(this.routeSources, new Map(), this.paintedRouteTiers);
    this.applyTiers(this.markerSources, new Map(), this.paintedActionTiers);
  }

  /**
   * The one value that is not per-feature: a cluster has no tier of its own, so it takes a flat
   * opacity that depends only on whether any focus is active. A constant replacing a constant,
   * which is the one style write mapbox does not answer with a source reload.
   */
  private writeClusterOpacity(focus: ResolvedFocus | null): void {
    if (isNil(this.map) || this.map._removed || isNil(this.registry)) return;

    const markers = this.registry.markers.layers;
    const resting = this.spiderfyOpen ? SPIDERFY_BACKDROP_OPACITY : undefined;
    const value = focus !== null ? CLUSTER_FOCUS_OPACITY : resting;

    // The count label rides its bubble: a cluster faded to context with its number still at full
    // strength reads as an emphasis on the number.
    this.setOpacity(markers.clustered, 'icon-opacity', value);
    this.setOpacity(markers.clusteredCount, 'text-opacity', value);
  }

  /** `undefined` hands the layer its declared value back. */
  private setOpacity(
    layerId: string,
    property: 'icon-opacity' | 'text-opacity',
    value: number | undefined
  ): void {
    if (isNil(this.map?.getLayer(layerId))) return;
    const key = baselineKey(layerId, property);
    if (!this.baseline.has(key)) return;
    this.map.setPaintProperty(layerId, property, value ?? this.baseline.get(key));
  }

  private buildTargets(registry: MapRegistry): FocusTarget[] {
    const routes = registry.routes.layers;
    const markers = registry.markers.layers;

    return [
      // The two layers draw the same source and split it opposite ways, each hiding what the
      // other draws. Which kind a layer owns is the only thing that differs between them.
      this.routeOpacity(routes.default, false),
      this.routeOpacity(routes.dashed, true),
      this.casingOpacity(routes.casing),
      this.symbolOpacity(routes.symbols),
      // Which layer carries the *lit* stops depends on the view: `default` while nothing is
      // filtered and grouping is off, `selected` once a hard filter swaps it in, `unclustered`
      // in the default grouped view, and `unclusteredSpiderfy` while an expanded cluster is
      // open. The stops none of them is lighting are drawn by `dim` alone, whichever of the four
      // is in play — one layer rather than a twin per view, because a dot needs neither the
      // source its pin was drawn from nor a place in the collision order.
      this.markerOpacity(markers.default, { standsDownWhenGrouped: true }),
      this.markerOpacity(markers.selected),
      this.markerOpacity(markers.unclustered, {
        litWhileSpiderOpen: SPIDERFY_BACKDROP_OPACITY
      }),
      this.markerOpacity(markers.unclusteredSpiderfy, { litWhileSpiderOpen: 1 }),
      this.dimOpacity(markers.dim),
      this.sequenceOpacity(markers.sequence),
      // `unselected` is absent on purpose. It is the stops a hard filter left out — unlit by
      // definition — and it draws them at its own declared strength whatever the focus says.
      // It needs no state-reading expression either: `readsState` is a symbol-layer hazard, and
      // `unselected` is a circle layer.
      //
      // The cluster pair keeps the plain constants `writeClusterOpacity` writes — a constant
      // replacing a constant is the one style write mapbox answers without a source reload —
      // so their state dependence has to come from a property the focus never writes. A halo
      // of zero width draws nothing, and an icon halo is ignored outright on a non-SDF icon.
      this.constant(markers.clustered, 'icon-halo-width', 0),
      this.constant(markers.clusteredCount, 'text-halo-width', 0),
      // Clusters are written per focus rather than through an expression, but their baselines
      // are captured with everything else so `setOpacity` has a resting value to hand back.
      this.clusterTarget(markers.clustered, 'icon-opacity'),
      this.clusterTarget(markers.clusteredCount, 'text-opacity')
    ];
  }

  /**
   * A route line's opacity: the lit route at full, every other line gone.
   *
   * Hidden rather than faded because a fade does not survive density. Overlapping lines
   * accumulate alpha — at `n` coincident lines a corridor composites to `1 − (1 − a)ⁿ` — so a
   * level faint enough to read as background on one line reads as a solid band on a trunk road,
   * which is exactly where the routes worth reading run.
   *
   * `drawsDashed` says which kind of feature this layer owns. **Both** branches have to honour
   * it, not just the lit one: the sibling layer draws the same source, so a value applied to a
   * feature this layer does not own renders that route a second time, over the top of the first.
   * Opacity is the only thing hiding it — neither layer filters by kind.
   */
  private routeOpacity(layerId: string, drawsDashed: boolean): FocusTarget {
    return {
      layerId,
      property: 'line-opacity',
      unset: 1,
      expression: (baseline) =>
        [
          'match',
          tierOf(),
          'lit',
          ownKindOnly(drawsDashed, 1),
          ['soft', 'dim'],
          ownKindOnly(drawsDashed, 0),
          baseline
        ] as mapboxgl.ExpressionSpecification
    };
  }

  /**
   * The white halo under a lit route, on a layer drawn below every other route layer. It is what
   * makes the focused line readable independently of what it crosses: the lit strand is
   * separated from the basemap's own roads and from the land fill, not only from other routes.
   *
   * Only solid routes get one. A halo is continuous, so under a dashed arc it fills the gaps and
   * the arc reads as one fat solid stripe — the opposite of what the dash is there to say.
   * Matching the dashes instead is not available: `line-dasharray` is measured in line widths,
   * so the same array on a casing 6px wider draws a visibly different period.
   *
   * Its layer declares `line-opacity: 0`, so the captured baseline hides it whenever no focus is
   * active and `detach()` hands that same 0 back.
   */
  private casingOpacity(layerId: string): FocusTarget {
    return {
      layerId,
      property: 'line-opacity',
      // Mapbox's default for an undeclared `line-opacity`, per this field's meaning. It is
      // unreachable while the layer declares `0`, and that declaration is load-bearing: drop it
      // and the baseline becomes this 1, painting the whole network white with no focus.
      unset: 1,
      expression: (baseline) =>
        [
          'match',
          tierOf(),
          'lit',
          ['case', ['==', ['get', 'dashed'], true], 0, 1],
          baseline
        ] as mapboxgl.ExpressionSpecification
    };
  }

  /**
   * The direction arrows along a route. The layer draws them for whichever route the pointer is
   * over, through mapbox's own `hover` feature state, and that expression survives here as the
   * baseline — the focus only takes the arrows away from a route it has hidden. Without this an
   * unlit route leaves a trail of arrows tracing a line that is no longer drawn.
   */
  private symbolOpacity(layerId: string): FocusTarget {
    return {
      layerId,
      property: 'icon-opacity',
      unset: 1,
      expression: (baseline) =>
        [
          'match',
          tierOf(),
          ['soft', 'dim'],
          0,
          baseline
        ] as mapboxgl.ExpressionSpecification
    };
  }

  /**
   * A coloured stop: drawn at the layer's own strength while the focus names it, and not drawn
   * at all otherwise — the `dim` layer, below every route line, puts a dot in its place.
   *
   * A `soft` action — one sharing a job with the focused stop — counts as named. Those are the
   * other legs of the same order, which is the relationship the focus exists to surface.
   *
   * `litWhileSpiderOpen` is the value to use instead while an expanded cluster is open. The
   * captured baseline is the spider-closed value, which for the spider's own legs is 0, so these
   * layers cannot read their lit value off the baseline alone.
   *
   * That value is also the fall-through, not the baseline: a stop no focus has named is at rest,
   * and a spider open with nothing focused is the ordinary case — reading the baseline there
   * would leave the spider's own legs at the 0 they declare and never draw them.
   */
  private markerOpacity(
    layerId: string,
    options: { litWhileSpiderOpen?: number; standsDownWhenGrouped?: boolean } = {}
  ): FocusTarget {
    return {
      layerId,
      property: 'icon-opacity',
      unset: 1,
      expression: (baseline) => {
        const resting =
          this.spiderfyOpen && !isNil(options.litWhileSpiderOpen)
            ? options.litWhileSpiderOpen
            : baseline;
        // What a stop no focus has named is worth. Normally the layer's own value — but the
        // unclustered `default` layer holds every stop on the map, and while they are grouped
        // the clusters are what stands for them, so it has to draw nothing until a focus
        // names something.
        const unnamed = options.standsDownWhenGrouped && this.grouped ? 0 : resting;
        return [
          'match',
          tierOf(),
          ['lit', 'soft'],
          resting,
          'dim',
          0,
          unnamed
        ] as mapboxgl.ExpressionSpecification;
      }
    };
  }

  /**
   * The dots: drawn only for the stops the focus pushed away, invisible at rest.
   *
   * One layer covers every view because it draws the unclustered source, which holds every stop
   * the map has — including the ones a cluster has swallowed, which no other layer draws. That is
   * what makes a focus in grouped mode show the whole network rather than only the stops that
   * happened to stand alone.
   *
   * It takes the spider's backdrop the same way the coloured layers do. Without that, a spider
   * opened over a focused route leaves the stops the focus pushed away drawn *stronger* than the
   * ones it kept — the coloured layer recedes to the backdrop while the dots do not.
   */
  private dimOpacity(layerId: string): FocusTarget {
    return {
      layerId,
      property: 'circle-opacity',
      unset: 0,
      expression: (baseline) => {
        const dim = this.spiderfyOpen
          ? UNLIT_DOT_OPACITY * SPIDERFY_BACKDROP_OPACITY
          : UNLIT_DOT_OPACITY;
        return [
          'match',
          tierOf(),
          'dim',
          dim,
          baseline
        ] as mapboxgl.ExpressionSpecification;
      }
    };
  }

  /**
   * A stop's number, drawn only while its route is the one on display. The layer holds every
   * stop of the map, so this expression is the whole of what selects them.
   */
  private sequenceOpacity(layerId: string): FocusTarget {
    return {
      layerId,
      property: 'text-opacity',
      unset: 1,
      expression: (baseline) =>
        [
          'case',
          ['==', ['feature-state', NUMBERED_KEY], true],
          baseline,
          0
        ] as mapboxgl.ExpressionSpecification
    };
  }

  /** Carries no expression; here only so the layer's resting opacity is captured. */
  private clusterTarget(layerId: string, property: PaintProperty): FocusTarget {
    return { layerId, property, unset: 1, expression: (baseline) => baseline };
  }

  /** A layer whose value the focus never changes, held as a state-reading expression. */
  private constant(layerId: string, property: PaintProperty, value: number): FocusTarget {
    return { layerId, property, unset: value, expression: () => readsState(value) };
  }

  private installExpressions(): void {
    if (isNil(this.map) || this.map._removed) return;

    for (const target of this.targets) {
      if (this.isFilterOwned(target)) continue;
      this.install(target);
    }
  }

  private reinstallSelected(): void {
    if (isNil(this.map) || this.map._removed || isNil(this.registry)) return;

    const selected = this.registry.markers.layers.selected;
    const target = this.targets.find((candidate) => candidate.layerId === selected);
    if (target) this.install(target);
  }

  private install(target: FocusTarget): void {
    if (isNil(this.map?.getLayer(target.layerId))) return;

    const key = baselineKey(target.layerId, target.property);
    if (!this.baseline.has(key)) return;

    this.map.setPaintProperty(
      target.layerId,
      target.property,
      target.expression(this.baseline.get(key)!)
    );
  }

  private isFilterOwned(target: FocusTarget): boolean {
    return (
      this.filterOwnsSelected && target.layerId === this.registry?.markers.layers.selected
    );
  }

  /**
   * Reads the targets' untouched values, before any expression is installed over them. Reading a
   * baseline lazily would capture whatever was already installed, so a re-attach without a
   * detach would nest one `match` inside the next.
   */
  private captureBaselines(): void {
    if (isNil(this.map)) return;

    for (const target of this.targets) {
      if (isNil(this.map.getLayer(target.layerId))) continue;
      const declared =
        this.map.getPaintProperty(target.layerId, target.property) ?? target.unset;
      this.baseline.set(baselineKey(target.layerId, target.property), declared);
    }
  }

  private restoreBaselines(): void {
    if (isNil(this.map) || this.map._removed) return;

    for (const target of this.targets) {
      if (isNil(this.map.getLayer(target.layerId))) continue;
      const key = baselineKey(target.layerId, target.property);
      if (!this.baseline.has(key)) continue;
      this.map.setPaintProperty(target.layerId, target.property, this.baseline.get(key));
    }
  }
}

/**
 * The tier a feature is in, `'rest'` when the focus has not named it. `coalesce` is what makes
 * the absent state readable: `match` needs a value of the same type in every branch, and an
 * unset feature state evaluates to null.
 */
function tierOf(): mapboxgl.ExpressionSpecification {
  return ['coalesce', ['feature-state', STATE_KEY], REST];
}

/**
 * `value` on the features a layer owns, 0 on the ones its sibling owns — the same split the two
 * route layers' declared opacities encode. Reproduced rather than read off the captured baseline
 * because the baseline carries the layer's own resting opacity, which the focus is replacing;
 * only the shape of the split survives.
 */
function ownKindOnly(
  drawsDashed: boolean,
  value: number
): mapboxgl.ExpressionSpecification {
  return [
    'case',
    ['==', ['get', 'dashed'], true],
    drawsDashed ? value : 0,
    drawsDashed ? 0 : value
  ];
}

/**
 * A value that never varies, written as an expression that reads feature state anyway.
 *
 * Mapbox gives every symbol layer a bucket of its own (`groupByLayout` keys them by layer id),
 * and `Tile.updateBuckets` hands `bucket.update` the bucket's *state-dependent* layers rather
 * than all of them the moment its source carries any feature state. A symbol layer whose paint
 * is all constants is not state-dependent, so it gets `layers[0] === undefined`, which
 * `SymbolPropertyBinderUBO.updateFeatures` stores as its layer while clearing the cached
 * uniforms — and `getConstantUniformValues` reads `this.layer.paint` on the next frame. The
 * result is an uncaught TypeError inside the render loop, on a layer nothing has touched.
 *
 * Reading state is what keeps a layer in its own `stateDependentLayers`. Every symbol layer
 * drawing a source this service writes state to therefore has to read it, the ones the focus
 * leaves alone included.
 */
function readsState(value: number): mapboxgl.ExpressionSpecification {
  return ['match', tierOf(), 'lit', value, value];
}

function baselineKey(layerId: string, property: PaintProperty): string {
  return `${layerId}|${property}`;
}
